import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  DialogHeader,
  BasicInformationForm,
  TemplateInfoCard,
  TreatmentsSection,
  useEditExperimentForm,
} from './EditExperimentDialogComponents';

const EditExperimentDialog = ({
  open,
  onClose,
  onSubmit,
  experiment,
  templates,
  templatesLoading,
  errorMessage,
}) => {
  const {
    formData,
    errors,
    submitting,
    setSubmitting,
    isActive,
    safeTrafficAllocation,
    templateName,
    selectedTemplate,
    templateVersions,
    getSafeTemplateVersion,
    handleInputChange,
    handleTreatmentChange,
    addTreatment,
    removeTreatment,
    validateForm,
  } = useEditExperimentForm({ experiment, templates });

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(experiment.id, formData);
    } catch (error) {
      // Error updating experiment
    } finally {
      setSubmitting(false);
    }
  };

  if (!experiment) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogHeader
        experimentName={experiment.name}
        onClose={onClose}
        disableClose={submitting}
      />

      <DialogContent sx={{ pt: 1 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <Grid
          container
          spacing={3}
          alignItems="flex-end"
          sx={{ mt: 1, width: '100%' }}
        >
          {/* Basic Information */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <BasicInformationForm
            formData={formData}
            errors={errors}
            templateName={templateName}
            safeTrafficAllocation={safeTrafficAllocation}
            isActive={isActive}
            onInputChange={handleInputChange}
          />

          <TemplateInfoCard selectedTemplate={selectedTemplate} />

          <TreatmentsSection
            treatments={formData.treatments}
            errors={errors}
            isActive={isActive}
            templateVersions={templateVersions}
            onAddTreatment={addTreatment}
            onRemoveTreatment={removeTreatment}
            onTreatmentChange={handleTreatmentChange}
            getSafeTemplateVersion={getSafeTemplateVersion}
          />
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || templatesLoading}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          {submitting ? 'Updating...' : 'Update Experiment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditExperimentDialog;
